import React, { useState } from 'react';
import styled from 'styled-components';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, FormControlLabel, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import BlockIcon from '@mui/icons-material/Block';

const AgeVerificationModal = ({ open, onClose, onConfirm, restrictedItems }) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleConfirm = () => {
    if (isConfirmed) {
      // Store verification in sessionStorage to avoid repeated prompts
      sessionStorage.setItem('ageVerified', 'true');
      sessionStorage.setItem('ageVerifiedTime', Date.now().toString());
      onConfirm();
    }
  };

  const handleCancel = () => {
    setIsConfirmed(false);
    onClose();
  };

  // Get unique restriction types
  const hasPrescriptionItems = restrictedItems.some(item => item.requiresPrescription);
  const hasAgeRestriction = restrictedItems.some(item => item.ageRestriction > 0);
  const hasControlledSubstance = restrictedItems.some(item => item.controlledSubstance);

  // Get max age restriction
  const maxAgeRestriction = Math.max(...restrictedItems.map(item => item.ageRestriction || 0));

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitleStyled>
        <WarningIcon style={{ color: '#f57c00', fontSize: '2rem', marginRight: '10px' }} />
        <Typography variant="h6" component="div">
          Age Verification & Restricted Items
        </Typography>
      </DialogTitleStyled>

      <DialogContent>
        <AlertBox>
          <Typography variant="body1" style={{ fontWeight: 600, marginBottom: '12px' }}>
            ⚠️ This order contains restricted healthcare items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You must verify that you meet the requirements to purchase these items.
          </Typography>
        </AlertBox>

        <RestrictionsHeader>Items in Your Order</RestrictionsHeader>
        <RestrictionsList>
          {restrictedItems.map((item, index) => (
            <ListItem key={index} style={{ padding: '8px 0' }}>
              <ListItemIcon style={{ minWidth: '40px' }}>
                {item.requiresPrescription ? (
                  <LocalPharmacyIcon style={{ color: '#d32f2f' }} />
                ) : item.ageRestriction > 0 ? (
                  <BlockIcon style={{ color: '#f57c00' }} />
                ) : (
                  <WarningIcon style={{ color: '#f57c00' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {item.requiresPrescription && (
                      <span style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                        ℞ Prescription Required
                      </span>
                    )}
                    {item.ageRestriction > 0 && (
                      <span style={{ color: '#f57c00', fontSize: '0.85rem' }}>
                        Age {item.ageRestriction}+ Restriction
                      </span>
                    )}
                    {item.controlledSubstance && (
                      <span style={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                        Controlled Substance
                      </span>
                    )}
                  </span>
                }
              />
            </ListItem>
          ))}
        </RestrictionsList>

        <RequirementsBox>
          <RequirementsTitle>Requirements</RequirementsTitle>
          <RequirementsList>
            {hasAgeRestriction && (
              <RequirementItem>
                <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                <span>You must be at least {maxAgeRestriction} years old</span>
              </RequirementItem>
            )}
            {hasPrescriptionItems && (
              <RequirementItem>
                <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                <span>Valid prescription required for indicated items</span>
              </RequirementItem>
            )}
            {hasControlledSubstance && (
              <RequirementItem>
                <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                <span>Additional verification may be required</span>
              </RequirementItem>
            )}
            <RequirementItem>
              <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
              <span>You may need to provide identification upon delivery</span>
            </RequirementItem>
          </RequirementsList>
        </RequirementsBox>

        <ConfirmationBox>
          <FormControlLabel
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                I confirm that I am of legal age and understand that I may need to provide
                valid documentation (prescription, ID) for the restricted items in this order.
              </Typography>
            }
          />
        </ConfirmationBox>

        <DisclaimerText>
          <Typography variant="caption" color="text.secondary">
            By confirming, you acknowledge that providing false information may result in
            order cancellation and potential legal consequences. The vendor reserves the
            right to verify your age and prescription status before completing the order.
          </Typography>
        </DisclaimerText>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <CancelButton onClick={handleCancel}>
          Cancel Order
        </CancelButton>
        <ProceedButton
          onClick={handleConfirm}
          disabled={!isConfirmed}
        >
          Proceed with Order
        </ProceedButton>
      </DialogActions>
    </Dialog>
  );
};

export default AgeVerificationModal;

// Styled Components
const DialogTitleStyled = styled(DialogTitle)`
  display: flex !important;
  align-items: center !important;
  background-color: #fff3e0 !important;
  border-bottom: 2px solid #f57c00 !important;
`;

const AlertBox = styled.div`
  background-color: #ffebee;
  border: 2px solid #d32f2f;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const RestrictionsHeader = styled.h4`
  margin: 20px 0 10px 0;
  color: #333;
  font-size: 1.1rem;
`;

const RestrictionsList = styled(List)`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 12px !important;
  margin-bottom: 20px;
`;

const RequirementsBox = styled.div`
  background-color: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const RequirementsTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #2e7d32;
  font-size: 1rem;
`;

const RequirementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #333;
`;

const ConfirmationBox = styled.div`
  background-color: #f5f5f5;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const DisclaimerText = styled.div`
  padding: 12px;
  background-color: #fafafa;
  border-left: 3px solid #9e9e9e;
  border-radius: 4px;
`;

const CancelButton = styled(Button)`
  text-transform: none !important;
  color: #d32f2f !important;
  border: 2px solid #d32f2f !important;
  padding: 10px 24px !important;
  font-weight: 600 !important;

  &:hover {
    background-color: #ffebee !important;
  }
`;

const ProceedButton = styled(Button)`
  text-transform: none !important;
  background-color: #4caf50 !important;
  color: white !important;
  padding: 10px 24px !important;
  font-weight: 600 !important;

  &:hover {
    background-color: #45a049 !important;
  }

  &:disabled {
    background-color: #e0e0e0 !important;
    color: #9e9e9e !important;
  }
`;


